/**
 * Renders a modal window.
 *
 * ## Fields
 * Name | Description
 * --- | ---
 * buttons | The array of `ButtonView`s in the footer area of the window. These are in the same order as the button options hashes in the `buttons` option.
 *
 * ## Events
 * Name | Arguments | Description
 * --- | --- | ---
 * shown | None. | Fired when the window is shown to the user.
 * hidden | None. | Fired when the window is hidden from the user.
 * @module app/views/ModalView
 */
import Backbone from "backbone";
import _ from "underscore";
import ButtonView from "core/views/ButtonView";
import $ from "jquery";
var ModalView = Backbone.View.extend({
  className: "modal fade",

  events: {
    "shown.bs.modal": function () {
      this.trigger("shown");
    },
    "hidden.bs.modal": function () {
      if (this.options.removeWhenHidden) {
        this.remove();
      }

      this.trigger("hidden");
    },
    keyup: function (evt) {
      if (evt.keyCode == 13 || evt.keyCode == 36) {
        //Enter or Return
        this.$(".btn-primary").click();
      }
    },
  },

  attributes: {
    tabindex: -1,
    role: "dialog",
    "aria-labelledby": "modal-label",
    "aria-hidden": "true",
  },

  template: _.template(
    '<div class="modal-dialog <%= modalClass%>"><div class="modal-content"><div class="modal-header"><% if(!locked) {%><button type="button" class="close close-dialog" aria-hidden="true" data-dismiss="modal">&times;</button><%}%><h4 class="modal-title" id="modal-label"><%= title%></h4></div><div class="modal-body"></div><div class="modal-footer"></div></div></div>'
  ),

  /**
   * Initializes the control.
   * @param  {object} options The options hash.
   * @param {string} options.title:'Title' The title for the window.
   * @param {string} options.text:'Text' The content text for the window.
   * @param {Array} options.buttons:[] An array of options hashes to be sent to the constructor of `ButtonView` to create the individual buttons for the window.
   * @param {Boolean} options.locked:false True if the user should be prevented from closing the modal view when it is shown, otherwise false.
   */
  initialize: function (options) {
    this.options = _.defaults({}, options, {
      title: "Title",
      text: "Text",
      modalClass: "",
      buttons: [],
      locked: false,
      removeWhenHidden: true,
    });
    this.buttons = [];
    _.each(
      this.options.buttons,
      function (buttonOptions) {
        this.buttons.push(new ButtonView(buttonOptions));
      },
      this
    );
    this._inProgress = false;

    if (this.options.el) {
      this._shown = true;

      if (this.$(".modal-body > .progress").length) {
        this._inProgress = true;
      }
    }
  },

  /**
   * Hide the window.
   * @return {ModalView} A reference to `this`.
   */
  hide: function () {
    if (this._shown) {
      var $el = this.$el;

      $el.modal("hide");
      setTimeout(function () {
        $el.modal("removeBackdrop");
        $(".modal-backdrop").remove();
      }, 1000);
      this._shown = false;
    }

    return this;
  },

  /**
   * Show the window.
   * @param {String} [title] An optional title which defaults to the title supplied during construction.
   * @return {ModalView} A reference to `this`.
   */
  show: function (title) {
    if (!this._shown) {
      title = title || this.options.title;
      this.setTitle(title);
      this.$el.modal("show");
      this._shown = true;
    }

    return this;
  },

  /**
   * Indicates whether or not the ModalView is currently being displayed
   * @return {Boolean} True if the ModalView is being displayed, otherwise false.
   */
  isShowing: function () {
    return this._shown;
  },

  /**
   * Sets the content of the window.
   * *Note*: Some browsers behave unexpectedly when content is added to a child element before the parent has been added to the DOM.
   * @param {string|jquery} content The content to place in the window. Window contents will be cleared before the update.
   */
  setContent: function (content) {
    this.$(".modal-body").html("");

    if (_.isString(content)) {
      this.$(".modal-body").html(content);
    } else {
      this.$(".modal-body").append(content);
    }
  },

  /**
   * Sets progress graphic on or off
   * *Note*: Some browsers behave unexpectedly when content is added to a child element before the parent has been added to the DOM.
   * @param {boolean} inProgress Turns the pogress graphic on or off
   */
  inProgress: function (inProgress) {
    if (inProgress) {
      if (this._inProgress === false) {
        //append the progress bar
        this.$(".modal-body").append(
          '<div class="progress progress-striped active" style="margin-top:7px; margin-bottom:0;"><div class="progress-bar" style="width: 100%"></div></div>'
        );
      }
      this._inProgress = true;
    } else {
      if (this._inProgress === true) {
        //remove the progress bar
        this.$el.find(".progress").remove(); //this.$el.remove('.progress') removed the entire modal
      }
      this._inProgress = false;
    }
  },

  /**
   * Sets the title of the modal window.
   * @param {string} title The title.
   */
  setTitle: function (title) {
    this.$(".modal-title").html(title);
  },

  remove: function () {
    if ($(".modal-backdrop").length > 1) {
      $($(".modal-backdrop")[0]).remove();
    } else {
      $(".modal-backdrop").remove();
      $("body").removeClass("modal-open");
    }
    this.$el.remove();
    this.trigger("remove");

    return this;
  },

  render: function () {
    var backdrop = this.options.locked ? "static" : true;

    if (!this.options.el) {
      this.$el.html(this.template(this.options));

      if (this.options.locked) {
        this.$el.attr({
          "data-backdrop": "static",
          "data-keyboard": "false",
        });
      }
    } else {
      this.setTitle(this.options.title);
    }
    _.each(
      this.buttons,
      function (button) {
        button.render();
        this.$(".modal-footer").append(button.$el);
      },
      this
    );
    this.$el.modal({
      backdrop: backdrop,
    });

    return this;
  },
});

export default ModalView;



//////////////////
// WEBPACK FOOTER
// ./public/js/core/views/ModalView.js
// module id = 73
// module chunks = 4